/**
 * Composable for interacting with Zendesk Messenger third
 * party APIs.
 */

export type ZendeskWarningType
  = | 'tracking-blocked'
    | 'script-blocked'
    | null;

export interface ZendeskStatus {
  isAvailable: boolean;
  isBlocked: boolean;
  warningType: ZendeskWarningType;
}

export function useZendeskMessenger() {
  /**
   * Check if Zendesk Messenger is available in the current environment
   * @returns {boolean} true if Zendesk Messenger is available, false otherwise
   */
  function isZendeskAvailable() {
    return typeof window !== 'undefined' && !!window.zE;
  }

  /**
   * Check for Zendesk availability including tracking prevention detection
   * @returns {Promise<ZendeskStatus>} Status object with detailed information
   */
  async function checkZendeskStatus(): Promise<ZendeskStatus> {
    const status: ZendeskStatus = {
      isAvailable: false,
      isBlocked: false,
      warningType: null
    };

    // Check if tracking prevention is blocking Zendesk
    const isTrackingBlocked = await detectTrackingPrevention();
    if (isTrackingBlocked) {
      status.isBlocked = true;
      status.warningType = 'tracking-blocked';
      return status;
    }

    // Basic availability check
    if (!isZendeskAvailable()) {
      status.isBlocked = true;
      status.warningType = 'script-blocked';
      return status;
    }

    return {
      isAvailable: true,
      isBlocked: false,
      warningType: null
    };
  }

  /**
   * Check if Zendesk resources are being blocked at the network level
   * @returns {Promise<boolean>} true if network-level blocking is detected
   */
  async function detectNetworkBlocking(): Promise<boolean> {
    // Try to fetch a known Zendesk resource to see if it's being blocked
    try {
      const zendeskUrls = [
        'https://static.zdassets.com/ekr/snippet.js',
        'https://ekr.zdassets.com/health'
      ];

      for (const url of zendeskUrls) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000);

          await fetch(url, {
            method: 'HEAD', // Use HEAD to minimize data transfer
            mode: 'no-cors', // Avoid CORS issues
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          // If we get here without an error, the request wasn't blocked
          return false;
        } catch (error) {
          console.warn('Network test failed for:', url, error);
        }
      }

      // If all requests failed, likely blocked
      return true;
    } catch (error) {
      // Assume blocked if we can't test
      console.warn('Network blocking detection error:', error);
      return true;
    }
  }

  /**
   * Check if tracking prevention might be blocking Zendesk
   * @returns {Promise<boolean>} true if tracking prevention is likely active
   */
  async function detectTrackingPrevention(): Promise<boolean> {
    // Network-level detection
    const networkBlocked = await detectNetworkBlocking();
    if (networkBlocked) {
      return true;
    }

    return false;
  }

  /**
   * Check if Zendesk Messenger is ready
   * @returns {Promise<boolean>} Promise that resolves when Zendesk Messenger is ready
   */
  function isZendeskReady() {
    return new Promise((resolve) => {
      if (isZendeskAvailable()) {
        resolve(true);
        return;
      }

      const check = setInterval(() => {
        if (isZendeskAvailable()) {
          clearInterval(check);
          resolve(true);
        }
      }, 50);

      // Timeout to prevent infinite waiting
      setTimeout(() => {
        clearInterval(check);
        resolve(false);
      }, 5000); // 5 second timeout
    });
  }

  /**
   * Set the locale for Zendesk Messenger
   * @param {string} locale - locale code to set for Zendesk Messenger
   */
  function setLocale(locale: string) {
    if (!isZendeskAvailable()) {
      console.warn('Zendesk is not available: cannot set locale');
      return;
    }

    window.zE('messenger:set', 'locale', locale);
  }

  /**
   * Set a callback function to be called when Zendesk Messenger is closed
   * @param {function} callback - function to call when Zendesk Messenger is closed
   */
  function setCloseEvent(callback: () => void) {
    if (!isZendeskAvailable()) {
      console.warn('Zendesk is not available: cannot set close event');
      return;
    }

    window.zE('messenger:on', 'close', callback);
  }

  /**
   * Trigger an open command on Zendesk Messenger
   */
  function openMessenger() {
    if (!isZendeskAvailable()) {
      console.warn('Zendesk is not available: cannot open messenger');
      return;
    }

    window.zE('messenger', 'open');
  }

  /**
   * Toggle the visibility of Zendesk Messenger
   * @param {boolean} show - true to show, false to hide
   */
  function toggleVisibility(show: boolean) {
    if (!isZendeskAvailable()) {
      console.warn('Zendesk is not available: cannot toggle visibility');
      return;
    }

    window.zE('messenger', show ? 'show' : 'hide');
  }

  return {
    setLocale,
    setCloseEvent,
    openMessenger,
    toggleVisibility,
    isZendeskAvailable,
    isZendeskReady,
    checkZendeskStatus,
    detectTrackingPrevention,
    detectNetworkBlocking
  };
}
